/**
 * Documents Service for Portal
 *
 * Handles fetching documents and download URLs for investors
 */

import { downloadUrlAsFile } from '@/services/documentService';
import type { FundDocumentContextType } from '@/types/document';
import { investorPortalApiClient } from '../../shared/utils/api-client';

/**
 * Document response interface
 */
export interface PortalDocument {
  id: string;
  name: string;
  original_filename: string;
  file_size: number;
  mime_type: string;
  upload_date: string;
  // Uploaded after launch and not yet opened by this contact
  is_new: boolean;
  tags: string[];
  entity_type:
    'FUND' | 'PORTFOLIO_COMPANY' | 'INVESTOR' | 'INVESTOR_NOTIFICATION';
  entity_id: string;
  entity_name: string;
  // Null for General documents, which belong to no fund
  fund_id: string | null;
  fund_name: string | null;
  context_type: FundDocumentContextType | null;
  // Null when directly in the fund folder
  folder_id: string | null;
}

/**
 * Folder in a fund's document tree
 */
export interface PortalDocumentFolder {
  id: string;
  name: string;
  // Null when directly in the fund folder
  parent_folder_id: string | null;
}

/**
 * Documents grouped by portfolio company interface
 */
export interface PortfolioCompanyDocuments {
  portfolio_company_id: string;
  portfolio_company_name: string;
  fund_id: string;
  fund_name: string;
  documents: PortalDocument[];
  document_count: number;
}

/**
 * Documents grouped by fund interface
 */
export interface FundDocuments {
  fund_id: string;
  fund_name: string;
  documents: PortalDocument[];
  document_count: number;
  // Portfolio company documents nested under fund
  portfolio_companies: PortfolioCompanyDocuments[];
  folders: PortalDocumentFolder[];
}

/**
 * All documents response interface
 */
export interface PortalDocumentsResponse {
  funds: FundDocuments[];
  general_documents: PortalDocument[];
  total_documents: number;
  published_fund_count: number;
}

/**
 * Download URL response interface
 */
interface DocumentDownloadUrlResponse {
  download_url: string;
  expires_at: string;
}

export class DocumentsService {
  /**
   * Get all documents across investor's funds, grouped by fund
   */
  static async getAllDocuments(): Promise<PortalDocumentsResponse> {
    return investorPortalApiClient.get<PortalDocumentsResponse>(
      '/portal/documents'
    );
  }

  /**
   * Get documents for a specific fund
   */
  static async getFundDocuments(fundId: string): Promise<FundDocuments> {
    return investorPortalApiClient.get<FundDocuments>(
      `/portal/documents/funds/${fundId}`
    );
  }

  /**
   * Get presigned download URL for a document
   */
  static async getDownloadUrl(
    documentId: string
  ): Promise<DocumentDownloadUrlResponse> {
    return investorPortalApiClient.get<DocumentDownloadUrlResponse>(
      `/portal/documents/${documentId}/download-url`
    );
  }

  /**
   * Download a document - reuses admin download logic
   */
  static async downloadDocument(
    documentId: string,
    filename: string
  ): Promise<void> {
    // Get portal-specific download URL, then use admin's download helper
    const { download_url } = await this.getDownloadUrl(documentId);

    if (!download_url) {
      throw new Error('Download URL not available');
    }

    await downloadUrlAsFile(download_url, filename);
  }
}
